
import { Component, Vue } from 'vue-property-decorator';
import {
  StockyMigrationModel,
  StockyRequestError,
} from '@/models/stocky_migration';
import type {
  StockySupplierPreview,
  StockySupplierImportResult,
  StockyPOJobStatus,
  StockyPOResults,
  StockyConflictMode,
} from '@/models/stocky_migration';
// Aliased: the getters below share these names, and `this`-less calls inside a
// class body resolve to the module scope, which reads as recursion otherwise.
import {
  normalizePOResults,
  buildStatTiles,
  completionHeadline as buildCompletionHeadline,
  completionSubtitle as buildCompletionSubtitle,
  dataWarnings as buildDataWarnings,
  detailPanelLabel as buildDetailPanelLabel,
  detailPanelIsError as isDetailPanelError,
} from '@/utils/stockyPoResults';
import type { StockyPOStatTile } from '@/utils/stockyPoResults';
import {
  isFailed,
  isQueued,
  isRunning,
  isTerminal,
  queueWaitColor,
  queueWaitCopy,
} from '@/utils/stockyJobStatus';
import type { QueueWaitCopy } from '@/utils/stockyJobStatus';

@Component
export default class StockyMigrationWizard extends Vue {
  private step = 0;
  private loading = false;
  private error: string | null = null;
  /** True when `error` came from a 409 (an import is already running), not a failure. */
  private errorIsConflict = false;

  /**
   * True when this screen is following an import it did not start — the store's
   * run lock was already held and the backend told us which job holds it.
   * Worth saying out loud: the progress on screen is someone else's run, so the
   * status filters and conflict mode chosen here are not the ones being applied.
   */
  private resumedExistingImport = false;

  /**
   * True when polling died on us rather than the import failing. The job is
   * marked `error` so the dialog stops treating it as active and gives the
   * close controls back, but "Import Failed" would be a guess — the import is
   * on the backend and very likely still running.
   */
  private pollingLostContact = false;

  // Step 0: Connection
  private stockyApiKey = '';
  private isConnected = false;
  private hasSavedKey = false;
  private savedKeyMask = '';

  // Step 1: Supplier preview
  private supplierPreview: StockySupplierPreview | null = null;
  private supplierConflictResolutions: Record<number, string> = {};
  private supplierImportResult: StockySupplierImportResult | null = null;
  private isSuppliersImported = false;

  // Step 2: PO status selection
  private selectedPOStatuses: string[] = [
    'draft-unarchived',
    'confirmed-unarchived',
    'draft-archived',
    'confirmed-archived',
  ];
  private poStatusOptions = [
    { value: 'confirmed-unarchived', label: 'Active', description: 'Confirmed, partially received, and received POs', icon: 'mdi-check-circle', color: 'success' },
    { value: 'draft-unarchived', label: 'Draft', description: 'POs still in draft status', icon: 'mdi-pencil-circle', color: 'grey' },
    { value: 'confirmed-archived', label: 'Archived (Confirmed)', description: 'Completed and archived confirmed POs', icon: 'mdi-archive', color: 'teal' },
    { value: 'draft-archived', label: 'Archived (Draft)', description: 'Archived draft POs', icon: 'mdi-archive-outline', color: 'blue-grey' },
  ];

  /**
   * What to do with POs already imported that the merchant has since received
   * against or edited here. Defaults to the non-destructive option.
   */
  private poConflictMode: StockyConflictMode = 'skip';
  private poConflictModeOptions = [
    {
      value: 'skip',
      label: 'Keep my Assisty changes',
      description: 'Leave those purchase orders exactly as they are. Nothing is imported over them.',
      icon: 'mdi-shield-check',
      color: 'success',
    },
    {
      value: 'overwrite',
      label: 'Replace them with Stocky data',
      description: 'Rebuild those purchase orders from Stocky, discarding receipts, returns and edits made in Assisty.',
      icon: 'mdi-alert-circle',
      color: 'warning',
    },
  ];

  // Step 3: PO import
  private jobId: string | null = null;
  private jobStatus: StockyPOJobStatus | null = null;
  private pollingInterval: number | null = null;

  /**
   * Guards against overlapping status requests.
   *
   * `setInterval` does not wait for its async callback, so a slow request can
   * still be in flight when a later one fails, records the terminal status and
   * stops the interval. The slow one then resolves and writes `processing` back
   * over it, leaving `isImportActive` true with no poller left to change it —
   * which in a modal means no close button, no Cancel, no action button and a
   * dead Esc key, so only a page reload gets the merchant out. Exactly the dead
   * end the terminal status below exists to prevent.
   *
   * Matches the guard on StockyDataMigration.vue, which runs the same import.
   */
  private isImportPollInFlight = false;
  /** When the import was submitted, used to age the queue-wait message. */
  private jobStartedAt: number | null = null;
  /** Advanced by the poll tick; the queue copy only needs minute resolution. */
  private queueElapsedMs = 0;

  private stepLabels = ['Connect', 'Suppliers', 'Select POs', 'Import'];

  private migrationFeatures = [
    'Suppliers and contact information',
    'Tax types and rates',
    'All purchase orders (draft, confirmed, received, archived)',
    'PO line items matched to your Shopify products',
    'Receive history',
    'Supplier-product relationships',
  ];

  private conflictResolutionOptions = [
    { text: 'Skip', value: 'skip' },
    { text: 'Update', value: 'update' },
    { text: 'Keep Both', value: 'keep_both' },
  ];

  private supplierTableHeaders = [
    { text: 'Name', value: 'name' },
    { text: 'Email', value: 'contactEmail' },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Action', value: 'action', sortable: false },
  ];


  async mounted() {
    try {
      const status = await StockyMigrationModel.getConnection();
      if (status.hasKey) {
        this.hasSavedKey = true;
        this.savedKeyMask = status.maskedKey || '';
        this.isConnected = true;
      }
    } catch {
      // Ignore — user will enter key manually
    }
  }

  get currentTitle(): string {
    switch (this.step) {
      case 0: return 'Migrate from Stocky';
      case 1: return 'Migrate from Stocky — Suppliers';
      case 2: return 'Migrate from Stocky — Select POs';
      case 3: return 'Migrate from Stocky — Import';
      default: return 'Migrate from Stocky';
    }
  }

  /** Queued, not yet picked up by a worker — no progress to show yet. */
  get isImportQueued(): boolean {
    return this.jobStatus !== null && isQueued(this.jobStatus.status);
  }

  get isImportRunning(): boolean {
    return this.jobStatus !== null && isRunning(this.jobStatus.status);
  }

  /**
   * Queued or running. This is what the dialog's close/navigation guards care
   * about: a queued job is just as much "in flight" as a running one, and
   * dismissing the wizard mid-queue would strand the merchant with no way back
   * to the progress screen.
   */
  get isImportActive(): boolean {
    return this.isImportQueued || this.isImportRunning;
  }

  /**
   * Terminal with nothing to carry forward. `handleAction` has no Step 3 branch,
   * so without this the button renders and does nothing at all.
   */
  get isImportFailed(): boolean {
    return this.jobStatus !== null && isFailed(this.jobStatus.status);
  }

  get importFailureHeadline(): string {
    // Losing the status endpoint says nothing about the import, which runs on
    // the backend regardless of whether this tab can see it.
    if (this.pollingLostContact) {
      return 'Import progress unavailable';
    }
    // A lost job record is not the same as a failed import — the import may
    // well have finished. Saying "Import Failed" there would be a guess.
    return this.jobStatus?.status === 'not_found' ? 'Import no longer tracked' : 'Import Failed';
  }

  get importFailureMessage(): string {
    if (this.jobStatus?.status === 'not_found') {
      return 'We lost track of this import — its progress record expired or was cleared. '
        + 'Any purchase orders it already imported are safe; re-running is safe too, because '
        + 'existing POs are updated rather than duplicated.';
    }
    return this.jobStatus?.error || 'An unknown error occurred.';
  }

  get queueWait(): QueueWaitCopy {
    return queueWaitCopy(this.queueElapsedMs);
  }

  get queueWaitColor(): string {
    return queueWaitColor(this.queueWait.severity);
  }

  get isImportDone(): boolean {
    return this.jobStatus !== null
      && (this.jobStatus.status === 'completed' || this.jobStatus.status === 'error');
  }

  /** Results with every counter defaulted, so the template never guards each field. */
  get poResults(): StockyPOResults {
    return normalizePOResults(this.jobStatus && this.jobStatus.results);
  }

  get poStatTiles(): StockyPOStatTile[] {
    return buildStatTiles(this.poResults);
  }

  get completionHeadline(): string {
    return buildCompletionHeadline(this.poResults);
  }

  get completionSubtitle(): string {
    return buildCompletionSubtitle(this.poResults);
  }

  /** Shown next to the protected count so the number is self-explanatory. */
  get hasProtectedPOs(): boolean {
    return this.poResults.skippedLocked > 0;
  }

  get dataWarnings(): string[] {
    return buildDataWarnings(this.poResults);
  }

  get detailPanelLabel(): string {
    return buildDetailPanelLabel(this.poResults);
  }

  get detailPanelIsError(): boolean {
    return isDetailPanelError(this.poResults);
  }

  get showActionButton(): boolean {
    if (this.step === 3 && this.isImportDone) return false;
    if (this.step === 3 && this.isImportActive) return false;
    if (this.step === 3 && this.isImportFailed) return false;
    return true;
  }

  get actionButtonText(): string {
    switch (this.step) {
      case 0:
        return this.isConnected ? 'Next' : 'Test Connection';
      case 1:
        return this.isSuppliersImported ? 'Next' : 'Import Suppliers';
      case 2:
        return 'Import Purchase Orders';
      default:
        return 'Next';
    }
  }

  get isActionDisabled(): boolean {
    if (this.loading) return true;
    if (this.step === 0 && !this.isConnected && !this.stockyApiKey.trim()) return true;
    if (this.step === 2 && this.selectedPOStatuses.length === 0) return true;
    return false;
  }

  stepClass(idx: number): string {
    if (idx < this.step) return 'step-indicator--done';
    if (idx === this.step) return 'step-indicator--active';
    return 'step-indicator--pending';
  }

  handleEsc() {
    if (!this.isImportActive) {
      this.$emit('close');
    }
  }

  goBack() {
    if (this.step > 0) {
      this.step -= 1;
    }
  }

  togglePOStatus(value: string) {
    const idx = this.selectedPOStatuses.indexOf(value);
    if (idx >= 0) {
      this.selectedPOStatuses.splice(idx, 1);
    } else {
      this.selectedPOStatuses.push(value);
    }
  }

  async handleAction() {
    this.error = null;

    if (this.step === 0) {
      if (this.isConnected) {
        this.loading = true;
        this.step = 1;
        await this.fetchSupplierPreview();
      } else {
        await this.testConnection();
      }
      return;
    }

    if (this.step === 1) {
      if (this.isSuppliersImported) {
        this.step = 2;
      } else {
        await this.importSuppliers();
      }
      return;
    }

    if (this.step === 2) {
      this.step = 3;
      await this.startPOImport();
    }
  }

  async testConnection() {
    this.loading = true;
    this.error = null;
    try {
      await StockyMigrationModel.connect(this.stockyApiKey);
      this.isConnected = true;
    } catch (err: any) {
      this.error = err.message || 'Failed to connect to Stocky. Please check your API key.';
    } finally {
      this.loading = false;
    }
  }

  async fetchSupplierPreview() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierPreview = await StockyMigrationModel.previewSuppliers();
      // Initialize conflict resolutions with default "skip"
      if (this.supplierPreview) {
        for (const supplier of this.supplierPreview.suppliers) {
          if (supplier.conflict) {
            this.$set(this.supplierConflictResolutions, supplier.stockyId, 'skip');
          }
        }
      }
    } catch (err: any) {
      this.error = err.message || 'Failed to load supplier preview.';
    } finally {
      this.loading = false;
    }
  }

  async importSuppliers() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierImportResult = await StockyMigrationModel.importSuppliers(
        this.supplierConflictResolutions
      );
      this.isSuppliersImported = true;
    } catch (err: any) {
      this.error = err.message || 'Failed to import suppliers.';
    } finally {
      this.loading = false;
    }
  }

  async startPOImport() {
    this.error = null;
    this.errorIsConflict = false;
    this.resumedExistingImport = false;
    try {
      const job = await StockyMigrationModel.importPurchaseOrders(
        this.selectedPOStatuses,
        this.poConflictMode,
      );
      await this.attachToJob(job.jobId, { resumed: false });
    } catch (err: any) {
      const conflict = err instanceof StockyRequestError && err.statusCode === 409;
      // 409 means another import already holds this store's run lock — usually
      // one started in another tab, or by a colleague. Nothing is broken and
      // nothing was duplicated, so when the backend names that run, follow it
      // instead of sending the merchant away to try again later.
      if (conflict && err.jobId) {
        this.resumedExistingImport = true;
        await this.attachToJob(err.jobId, { resumed: true });
        return;
      }
      // No job id: the lock is held by a run that died before it created one.
      // There is nothing to watch, so this stays the message it always was.
      this.errorIsConflict = conflict;
      this.error = err.message || 'Failed to start purchase order import.';
    }
  }

  startPolling(jobId: string) {
    // Reset in case a previous run left it set.
    this.isImportPollInFlight = false;
    this.pollingInterval = window.setInterval(async () => {
      if (this.isImportPollInFlight) {
        return;
      }
      this.isImportPollInFlight = true;
      try {
        const status = await StockyMigrationModel.getImportStatus(jobId);
        this.jobStatus = status;

        if (this.jobStartedAt !== null) {
          this.queueElapsedMs = Date.now() - this.jobStartedAt;
        }

        if (isTerminal(status.status)) {
          this.stopPolling();
        }

        // The job record is gone — Redis eviction, or a stale id from a
        // previous session. Polling it forever would leave the wizard on a
        // screen that can never change, so say so instead.
        if (status.status === 'not_found') {
          this.error = 'This import is no longer being tracked. Re-run the import to start a new one.';
        }
      } catch (err) {
        // Polling is the only thing that can move the job out of an active
        // state, so stopping it without recording a terminal one strands the
        // dialog: `isImportActive` stays true, which hides the header close
        // button, the footer Cancel button and the action button, and makes
        // Esc a no-op. Nothing is left running to change it, so only a page
        // reload gets the merchant out. Fail the job explicitly instead.
        this.pollingLostContact = true;
        this.jobStatus = {
          status: 'error',
          error: 'We could not read this import\'s progress. The import itself is most likely '
            + 'still running — reopen this wizard shortly to check on it. Re-running is safe '
            + 'either way, because existing purchase orders are updated rather than duplicated.',
        };
        this.stopPolling();
      } finally {
        this.isImportPollInFlight = false;
      }
    }, 2000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  navigateTo(routeName: string) {
    this.$emit('close');
    this.$router.push({ name: routeName }).catch(() => {});
  }

  beforeDestroy() {
    this.stopPolling();
  }

  /**
   * Point the wizard at a running import and follow it to completion.
   *
   * `resumed` marks an import that was already under way. Its real start time is
   * unknown — the backend reports no createdAt — so `jobStartedAt` stays null,
   * which keeps the queue-wait copy silent rather than having it report a wait
   * measured from the moment we attached.
   */
  private async attachToJob(jobId: string, { resumed }: { resumed: boolean }) {
    this.jobId = jobId;
    this.pollingLostContact = false;
    // Mirror what the backend has actually recorded — enqueued, waiting for a
    // worker. Seeding a status the backend never sends is what used to make
    // the first poll flip the wizard into an unrendered state.
    this.jobStatus = { status: 'created' };
    this.jobStartedAt = resumed ? null : Date.now();
    this.queueElapsedMs = 0;

    if (resumed) {
      // The run is already in progress, so read its real state now rather than
      // showing "Job Initialized" until the first poll comes round.
      try {
        this.jobStatus = await StockyMigrationModel.getImportStatus(jobId);
      } catch (statusError) {
        // Keep the seeded status; the poll below will correct it.
      }
    }

    this.startPolling(jobId);
  }
}
